import { QueryObserverBaseResult } from '@tanstack/query-core';
import { UseMutateAsyncFunction } from '@tanstack/react-query';
import React from 'react';
import { FieldValues, UseFormReturn } from 'react-hook-form';
import { InputHandlerManager } from './input-handle-manager';
import { InputProps } from './integrated-form-controls/integrated-form-control';

export type IntegratedFormMutation<T = any> = {
    isError: boolean;
    isLoading: boolean;
    mutate: (args: T) => void;
    mutateAsync: UseMutateAsyncFunction<unknown, unknown, T, unknown>;
    error?: unknown | null;
};

export type IntegratedFormQuery<TData = unknown, TError = unknown> = Partial<
    QueryObserverBaseResult<TData, TError>
> &
    Pick<QueryObserverBaseResult<TData, TError>, 'isError' | 'isInitialLoading'>;

export type IntegratedFormOnSubmit<T extends FieldValues> = (formShape: T) => any;

export type GetInputProps = (props?: { isDisabled?: boolean }) => Pick<InputProps, 'isDisabled'>;

export type GetGenericButtonProps = (props?: {
    isDisabled?: boolean;
}) => Pick<InputProps, 'isDisabled'>;

export type GetSaveButtonProps = (props?: {
    isDisabled?: boolean;
    isLoading?: boolean;
}) => Pick<InputProps, 'isDisabled' | 'isLoading'>;

export type GetFormInputButtonProps = (props?: {
    isDisabled?: boolean;
}) => Pick<InputProps, 'isDisabled'>;

export type IntegratedForm<T extends FieldValues> = {
    query: IntegratedFormQuery;
    mutation: IntegratedFormMutation;
    getGenericButtonProps: GetGenericButtonProps;
    getCancelButtonProps: GetGenericButtonProps;
    getSaveButtonProps: GetSaveButtonProps;
    getFormInputButtonProps: GetFormInputButtonProps;
    getInputProps: GetInputProps;
    onSubmit: (
        e: React.BaseSyntheticEvent<object, unknown, unknown> | undefined,
        onSuccess?: () => void,
        onError?: (err: unknown) => void
    ) => void;
    form: UseFormReturn<T, any>;
    withEditable?: true;
    inputSize?: InputProps['size'];
    inputHandleManager: InputHandlerManager<Parameters<UseFormReturn<T, any>['setValue']>[0]>;
    mode?: 'read-only';
    fuzzyMatchingEnabled?: boolean;
    isSubmitting?: boolean;
};

export const IntegratedFormContext = React.createContext<IntegratedForm<any> | undefined>(
    undefined
);
